import { get } from 'lodash';
import { resolve, reject } from 'redux-optimist-promise';
import type { CalendarData, PricesByDays, BookableDatesData } from 'Store/calendar/calendar.helper';
import {
  INITIAL_MONTH,
  getDefaultSelectedMonth,
  getPricesByDays,
  getSelectedMonth,
} from 'Store/calendar/calendar.helper';
import {
  FETCH_CALENDAR,
  FETCH_CALENDAR_TRIP_PRICE,
  FETCH_BOOKABLE_DATES,
  CALENDAR_CHANGE_MONTH,
  CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE,
  CALENDAR_SET_INITIAL_DURATION,
  CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION,
  CLEAR_CALENDAR,
  TRIP_INFORMATION_SET_DEPARTURE_DATE,
  SYNC_WITH_LOCATION,
} from 'Store/constants';
import createReducer from 'Store/utils/createReducer';
import { QUERY_DEPARTURE_DATE, QUERY_DATE_FORMAT } from 'Utils/uri/constants';

const EMPTY_BOOKABLE_DATES = {} as BookableDatesData;
const EMPTY_CALENDAR = {} as CalendarData;
const EMPTY_TRIP_PRICE = {} as ProductPrice;

type CalendarRequestResolvedAction = OptimisticResolvedAction<{
  calendar: CalendarData;
}>;
type BookableDatesRequestResolvedAction = OptimisticResolvedAction<{
  bookableDates: BookableDatesData;
}>;
export type CalendarState = {
  blockInputDepartureDate: boolean;
  bookableDateLastRequestId: number;
  bookableDates: BookableDatesData;
  bookableDatesCriteria: any;
  byDays: PricesByDays;
  calendarCriteria: any;
  calendar: CalendarData;
  departureCity: string | null | undefined;
  initialDuration: number | null | undefined;
  errorResponse: {
    errors?: any;
  };
  isLoading: boolean;
  bookableDatesLoading: boolean;
  isPriceCalendarSelection: boolean;
  lastRequestId: number;
  productId: string | null | undefined;
  tripPrice: ProductPrice;
  tripPriceIsLoading: boolean;
  tripPriceLastRequestId: number;
  selectedMonth: number;
};
export const initialState: CalendarState = {
  blockInputDepartureDate: true,
  bookableDateLastRequestId: -1,
  bookableDates: EMPTY_BOOKABLE_DATES,
  bookableDatesCriteria: {},
  byDays: {},
  calendar: EMPTY_CALENDAR,
  calendarCriteria: {},
  departureCity: null,
  initialDuration: null,
  isLoading: true,
  bookableDatesLoading: false,
  isPriceCalendarSelection: false,
  errorResponse: {},
  selectedMonth: getDefaultSelectedMonth(),
  lastRequestId: -1,
  productId: null,
  tripPrice: EMPTY_TRIP_PRICE,
  tripPriceLastRequestId: -1,
  tripPriceIsLoading: false,
};
export const calendarReducer = createReducer<CalendarState>(initialState, {
  [SYNC_WITH_LOCATION](
    state: CalendarState,
    action: {
      payload: LocationChangePayload;
    },
  ): CalendarState {
    const {
      payload: {
        location: { queryParams = {} },
      },
    } = action;

    if (!queryParams[QUERY_DEPARTURE_DATE]) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(
      queryParams[QUERY_DEPARTURE_DATE] || state.selectedMonth,
      QUERY_DATE_FORMAT,
    );
    return { ...state, selectedMonth };
  },

  [FETCH_CALENDAR](
    state: CalendarState,
    action: OptimisticAction<
      {
        departureCity?: string;
        productId?: string;
        options?: Record<string, unknown>;
      },
      {}
    >,
  ): CalendarState {
    const payload = action.payload ?? {};
    return {
      ...state,
      bookableDates: EMPTY_BOOKABLE_DATES,
      calendarCriteria: payload.options || {},
      departureCity: payload.departureCity,
      isLoading: true,
      lastRequestId: get(action, 'optimist.id', -1),
      productId: payload.productId,
    };
  },

  [resolve(FETCH_CALENDAR)](
    state: CalendarState,
    action: CalendarRequestResolvedAction,
  ): CalendarState {
    const id = get(action, 'optimist.id', -1);
    const calendar = action.payload?.calendar;

    // If there is another request in progress
    if (id !== state.lastRequestId || !calendar) {
      return state;
    }

    const { selectedMonth } = state;
    return {
      ...state,
      byDays: getPricesByDays(calendar),
      calendar,
      selectedMonth: getSelectedMonth(calendar, selectedMonth),
      isLoading: false,
    };
  },

  [reject(FETCH_CALENDAR)](state: CalendarState, action: OptimisticRejectedAction): CalendarState {
    const errors = action.payload.errors || '';
    return {
      ...state,
      byDays: {},
      errorResponse: {
        errors,
      },
      isLoading: false,
    };
  },

  [FETCH_BOOKABLE_DATES](
    state: CalendarState,
    action: OptimisticAction<{ options?: Record<string, unknown> }, {}>,
  ): CalendarState {
    const payload = action.payload ?? {};
    return {
      ...state,
      bookableDatesCriteria: payload.options || {},
      bookableDatesLoading: true,
      bookableDateLastRequestId: get(action, 'optimist.id', -1),
    };
  },

  [resolve(FETCH_BOOKABLE_DATES)](
    state: CalendarState,
    action: BookableDatesRequestResolvedAction,
  ): CalendarState {
    const id = get(action, 'optimist.id', -1);
    const bookableDates = action.payload?.bookableDates;

    if (id !== state.bookableDateLastRequestId || !bookableDates) {
      return state;
    }

    return { ...state, bookableDates, bookableDatesLoading: false };
  },

  [reject(FETCH_BOOKABLE_DATES)](
    state: CalendarState,
    action: OptimisticRejectedAction,
  ): CalendarState {
    const errors = action.payload.errors || '';
    return {
      ...state,
      bookableDates: EMPTY_BOOKABLE_DATES,
      errorResponse: {
        errors,
      },
      bookableDatesLoading: false,
    };
  },

  [CALENDAR_CHANGE_MONTH](
    state: CalendarState,
    action: Action<{
      selectedMonth: number;
    }>,
  ): CalendarState {
    const selectedMonth = action.payload.selectedMonth || INITIAL_MONTH;
    return { ...state, selectedMonth };
  },

  [CLEAR_CALENDAR](): CalendarState {
    return initialState;
  },

  [TRIP_INFORMATION_SET_DEPARTURE_DATE](
    state: CalendarState,
    action: Action<{
      departureDate: string;
    }>,
  ): CalendarState {
    if (!action.payload.departureDate) {
      return state;
    }

    const selectedMonth = getDefaultSelectedMonth(action.payload.departureDate);
    return { ...state, selectedMonth };
  },

  [CALENDAR_SET_BLOCK_INPUT_DEPARTURE_DATE](
    state: CalendarState,
    action: Action<{
      blockInputDepartureDate: boolean;
    }>,
  ): CalendarState {
    return { ...state, blockInputDepartureDate: action.payload.blockInputDepartureDate };
  },

  [CALENDAR_SET_INITIAL_DURATION](
    state: CalendarState,
    action: Action<{
      duration: number;
    }>,
  ): CalendarState {
    return { ...state, initialDuration: action.payload.duration };
  },

  [CALENDAR_SET_IS_PRICE_CALENDAR_SELCTION](
    state: CalendarState,
    action: Action<{
      isPriceCalendarSelection: boolean;
    }>,
  ): CalendarState {
    return { ...state, isPriceCalendarSelection: action.payload.isPriceCalendarSelection };
  },

  [FETCH_CALENDAR_TRIP_PRICE](state: CalendarState, action: OptimisticAction<{}, {}>) {
    return {
      ...state,
      tripPriceIsLoading: true,
      tripPriceLastRequestId: get(action, 'optimist.id', -1),
    };
  },

  [resolve(FETCH_CALENDAR_TRIP_PRICE)](
    state: CalendarState,
    action: PriceTicketRequestResolvedAction,
  ): CalendarState {
    const id = get(action, 'optimist.id', -1);
    const tripPrice = action.payload?.price ?? EMPTY_TRIP_PRICE;

    if (id !== state.tripPriceLastRequestId) {
      return state;
    }

    return { ...state, tripPrice: tripPrice as ProductPrice, tripPriceIsLoading: false };
  },

  [reject(FETCH_CALENDAR_TRIP_PRICE)](state: CalendarState): CalendarState {
    return { ...state, tripPrice: EMPTY_TRIP_PRICE, tripPriceIsLoading: false };
  },
});
